'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import {
  ArrowRight, Coffee, Briefcase, Building2,
  HeartPulse, GraduationCap, ShoppingBag, Scissors, Dumbbell,
  Server, Shield, Zap, RefreshCw, MessageSquare, Calendar,
  CreditCard, Users, Globe, BarChart3,
} from 'lucide-react'
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'
import PlayIcon from '@/components/ui/PlayIcon'
import ComingSoonModal from '@/components/ui/ComingSoonModal'

const fadeUp = (i?: number) => ({
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: (i || 0) * 0.08, ease: EASE_CUSTOM } },
})

/* ── Soluciones (productos) ── */
const SOLUCIONES = [
  { label: 'Sistema de Reservas', slug: 'reservas', icon: Calendar, desc: 'Agenda online 24/7 con pagos', color: 'blue' },
  { label: 'Pasarelas de Pago', slug: 'pagos', icon: CreditCard, desc: 'Stripe, Bizum y facturación auto', color: 'green' },
  { label: 'CRM a Medida', slug: 'crm', icon: Users, desc: 'Gestión de clientes centralizada', color: 'indigo' },
  { label: 'Automatizaciones', slug: 'automatizaciones', icon: Zap, desc: 'Email, WhatsApp y tareas auto', color: 'violet' },
  { label: 'Web de Captación', slug: 'web', icon: Globe, desc: 'Landing pages que captan y convierten', color: 'sky' },
  { label: 'Dashboard & Analytics', slug: 'dashboard', icon: BarChart3, desc: 'Métricas en tiempo real', color: 'orange' },
]

const SOLUCION_COLOR: Record<string, { iconBg: string; iconBorder: string; iconText: string }> = {
  blue:   { iconBg: 'bg-blue-50',   iconBorder: 'border-blue-100',   iconText: 'text-blue-600' },
  green:  { iconBg: 'bg-green-50',  iconBorder: 'border-green-100',  iconText: 'text-green-600' },
  indigo: { iconBg: 'bg-indigo-50', iconBorder: 'border-indigo-100', iconText: 'text-indigo-600' },
  violet: { iconBg: 'bg-violet-50', iconBorder: 'border-violet-100', iconText: 'text-violet-600' },
  sky:    { iconBg: 'bg-sky-50',    iconBorder: 'border-sky-100',    iconText: 'text-sky-600' },
  orange: { iconBg: 'bg-orange-50', iconBorder: 'border-orange-100', iconText: 'text-orange-600' },
}

/* ── Sectores ── */
const SECTORS = [
  { label: 'Hostelería', slug: 'hosteleria', icon: Coffee, desc: 'Reservas, carta digital, cocina y TPV', demoUrl: null as string | null },
  { label: 'Peluquerías', slug: 'peluqueria', icon: Scissors, desc: 'Agenda, fidelización y cobro automático', demoUrl: null as string | null },
  { label: 'Gestorías', slug: 'gestoria', icon: Briefcase, desc: 'Portal clientes, documentos y tareas', demoUrl: null as string | null },
  { label: 'Clínicas', slug: 'clinicas', icon: HeartPulse, desc: 'Agenda médica, historial y facturación', demoUrl: null as string | null },
  { label: 'Construcción', slug: 'construccion', icon: Building2, desc: 'Presupuestos, partes y certificaciones', demoUrl: null as string | null },
  { label: 'Formación', slug: 'formacion', icon: GraduationCap, desc: 'Campus virtual, matrículas y pagos', demoUrl: null as string | null },
  { label: 'E-commerce', slug: 'ecommerce', icon: ShoppingBag, desc: 'Tienda, inventario y logística', demoUrl: null as string | null },
  { label: 'Deporte / Fitness', slug: 'deporte', icon: Dumbbell, desc: 'Reserva clases, bonos y membresías', demoUrl: null as string | null },
]

/* ── Qué incluye ── */
const INCLUDES = [
  { icon: Server, text: 'Hosting y mantenimiento incluido' },
  { icon: Shield, text: 'Backups diarios y seguridad SSL' },
  { icon: RefreshCw, text: 'Actualizaciones automáticas' },
  { icon: Zap, text: 'Soporte técnico continuo' },
  { icon: MessageSquare, text: 'Chat y email de soporte' },
  { icon: Calendar, text: 'Onboarding personalizado' },
]

export default function SaasClient() {
  const [demoModalOpen, setDemoModalOpen] = useState(false)
  const [demoProductName, setDemoProductName] = useState('')

  const handleDemoClick = (e: React.MouseEvent, name: string, demoUrl: string | null) => {
    e.preventDefault()
    e.stopPropagation()
    if (demoUrl) {
      window.open(demoUrl, '_blank')
    } else {
      setDemoProductName(name)
      setDemoModalOpen(true)
    }
  }

  return (
    <>
    <ComingSoonModal
      open={demoModalOpen}
      onClose={() => setDemoModalOpen(false)}
      productName={demoProductName}
    />
    <main className="pt-24 pb-20">
      {/* Hero */}
      <section className="relative z-10 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div custom={0} variants={fadeUp()} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200/60 bg-blue-50/70 px-4 py-1.5 backdrop-blur-sm mb-6"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-600" />
            </span>
            <span className="text-xs font-semibold tracking-wide text-blue-700">Software as a Service</span>
          </motion.div>

          <motion.h1 custom={1} variants={fadeUp()} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight text-slate-900 mb-5"
          >
            Tu software.
            <br />
            <span className="text-blue-600">
              Diseñado para tu sector.
            </span>
          </motion.h1>

          <motion.p custom={2} variants={fadeUp()} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="text-lg text-slate-500 font-light max-w-2xl mx-auto mb-10"
          >
            Elige tu solución o tu sector y descubre cómo podemos digitalizar tu negocio. Sin inversión inicial, sin preocuparte por el hosting ni las actualizaciones.
          </motion.p>
        </div>
      </section>

      {/* ── Soluciones grid ── */}
      <section className="relative z-10 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div custom={0} variants={fadeUp()} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mb-10"
          >
            <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-3 text-center">
              Soluciones
            </div>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight text-slate-900 mb-3 text-center">
              ¿Qué necesita tu negocio?
            </h2>
            <p className="text-slate-500 font-light text-center max-w-xl mx-auto">
              Cada módulo se puede combinar o usar de forma independiente. Elige lo que necesitas ahora.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SOLUCIONES.map((sol, i) => {
              const Icon = sol.icon
              const c = SOLUCION_COLOR[sol.color] || SOLUCION_COLOR.blue
              return (
                <motion.div key={sol.slug} custom={i + 1} variants={fadeUp()}
                  initial="hidden" whileInView="show" viewport={{ once: true }}
                >
                  <Link
                    href={`/soluciones/${sol.slug}`}
                    className="glass-card rounded-2xl p-6 flex flex-col items-start group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 block h-full"
                  >
                    <div className={`w-11 h-11 rounded-xl ${c.iconBg} border ${c.iconBorder} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform`}>
                      <Icon className={`w-5 h-5 ${c.iconText}`} />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1 group-hover:text-blue-700 transition-colors">{sol.label}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">{sol.desc}</p>
                    <div className="flex items-center gap-3 mt-auto w-full">
                      <span className="text-xs font-semibold text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                        Ver solución <ArrowRight className="w-3 h-3" />
                      </span>
                      <button
                        onClick={(e) => handleDemoClick(e, sol.label, null)}
                        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-blue-200/80 bg-blue-50/70 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all active:scale-95"
                      >
                        <PlayIcon className="w-2.5 h-2.5 fill-blue-600" />
                        Demo
                      </button>
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Sectores grid ── */}
      <section className="relative z-10 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div custom={0} variants={fadeUp()} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mb-10"
          >
            <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-3 text-center">
              Sectores
            </div>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight text-slate-900 mb-3 text-center">
              Elige tu sector
            </h2>
            <p className="text-slate-500 font-light text-center max-w-xl mx-auto">
              Cada software está diseñado específicamente para las necesidades de tu industria.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SECTORS.map((sector, i) => {
              const Icon = sector.icon
              return (
                <motion.div key={sector.slug} custom={i + 1} variants={fadeUp()}
                  initial="hidden" whileInView="show" viewport={{ once: true }}
                >
                  <Link
                    href={`/sectores/${sector.slug}`}
                    className="glass-card rounded-2xl p-6 flex flex-col items-start group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 block h-full"
                  >
                    <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1 group-hover:text-blue-700 transition-colors">{sector.label}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed mb-3">{sector.desc}</p>
                    <div className="flex items-center gap-3 mt-auto w-full">
                      <span className="text-xs font-semibold text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                        Ver solución <ArrowRight className="w-3 h-3" />
                      </span>
                      <button
                        onClick={(e) => handleDemoClick(e, sector.label, sector.demoUrl)}
                        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-blue-200/80 bg-blue-50/70 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all active:scale-95"
                      >
                        <PlayIcon className="w-2.5 h-2.5 fill-blue-600" />
                        Demo
                      </button>
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </div>

          {/* Other sector CTA */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 text-center"
          >
            <p className="text-sm text-slate-500 mb-3">¿Tu sector no aparece?</p>
            <Link href="/contacto"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-all active:scale-95"
            >
              Cuéntanos y lo construimos →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What's included */}
      <section className="relative z-10 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2 custom={0} variants={fadeUp()} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-8 text-center"
          >
            Todo incluido en cada solución
          </motion.h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {INCLUDES.map((item, i) => {
              const Icon = item.icon
              return (
                <motion.div key={item.text} custom={i + 1} variants={fadeUp()} initial="hidden" whileInView="show"
                  viewport={{ once: true }}
                  className="glass-card rounded-2xl p-4 text-center"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-xs font-medium text-slate-700 leading-snug">{item.text}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="relative z-10 pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="glass-card rounded-3xl p-10 sm:p-14 shadow-xl space-y-6"
          >
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              ¿No sabes por dónde empezar?
            </h2>
            <p className="text-slate-500 text-lg max-w-md mx-auto leading-relaxed">
              Reserva una llamada gratuita de 30 minutos. Te ayudamos a identificar qué solución encaja con tu negocio.
            </p>
            <a
              href="https://calendly.com/innovatheratech/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-white font-bold text-[15px] shadow-[0_4px_20px_rgba(37,99,235,0.3)] transition-all active:scale-95 bg-blue-600 hover:bg-blue-700"
            >
              Agenda tu diagnóstico gratis
              <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-[11px] text-slate-400 font-mono">
              Sin compromiso · 30 minutos · 100% gratuito
            </p>
          </motion.div>
        </div>
      </section>
    </main>
    </>
  )
}
